import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, IndianRupee, ShoppingCart, Trash2, Minus, Plus, TicketPercent, Check, X, StickyNote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { payWithRazorpay } from "@/lib/razorpay";
import { API_BASE } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { SIZE_LABELS, specialityName, variantName, cartShippingInr, unitPriceInr } from "@/data/coffeeCatalog";
import { getCart, setBags, removeFromCart, clearCart } from "@/lib/cart";

const withFreshPrices = (items) =>
  items.map((c) => ({ ...c, unit_price: unitPriceInr(c.speciality, c.size, c.blend_arabica_pct) }));

export default function CoffeeCheckout() {
  const [cart, setCart] = useState(withFreshPrices(getCart()));
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [pincode, setPincode] = useState("");
  const [country, setCountry] = useState("India");
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [voucherError, setVoucherError] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [member, setMember] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const navigate = useNavigate();
  const { formatInr, isInr } = useCurrency();

  useEffect(() => {
    const sync = () => setCart(withFreshPrices(getCart()));
    window.addEventListener("cart-updated", sync);
    axios.get(`${API_BASE}/member/auth/me`, { withCredentials: true })
      .then((r) => {
        setMember(r.data);
        if (r.data?.name) setName((n) => n || r.data.name);
        if (r.data?.email) setEmail((e) => e || r.data.email);
      })
      .catch(() => {});
    return () => window.removeEventListener("cart-updated", sync);
  }, []);

  const zone = country.trim().toLowerCase() === "india" ? "india" : "international";
  const subtotal = cart.reduce((s, c) => s + c.unit_price * (c.bags || 1), 0);
  const totalGrams = cart.reduce((s, c) => s + parseInt(c.size, 10) * (c.bags || 1), 0);
  const shipping = cart.length ? cartShippingInr(zone, totalGrams) : 0;
  const discount = voucher
    ? voucher.discount_type === "percent"
      ? Math.round((subtotal * voucher.value) / 100 * 100) / 100
      : Math.min(voucher.value, subtotal)
    : 0;
  const gst = zone === "india" ? Math.round(subtotal * 5) / 100 : 0;
  const total = useMemo(
    () => Math.max(subtotal + gst + shipping - discount - (redeemPoints || 0), 1),
    [subtotal, gst, shipping, discount, redeemPoints]
  );

  const applyVoucher = async () => {
    if (!voucherInput.trim()) return;
    setVoucherLoading(true);
    setVoucherError("");
    try {
      const r = await axios.post(`${API_BASE}/coffee/voucher/validate`, { code: voucherInput.trim() });
      setVoucher(r.data);
    } catch (err) {
      setVoucher(null);
      setVoucherError(err?.response?.data?.detail || "Invalid voucher code");
    } finally {
      setVoucherLoading(false);
    }
  };

  const validate = () => {
    if (!name.trim()) return "Please enter your name";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return "Please enter a valid email";
    if (!phone.trim() || phone.replace(/\D/g, "").length < 7) return "Please enter a valid phone number";
    if (!line1.trim()) return "Please enter your delivery address";
    if (!city.trim()) return "Please enter your city";
    if (zone === "india" && !/^\d{6}$/.test(pincode)) return "Please enter a valid 6-digit pincode";
    if (zone === "international" && !pincode.trim()) return "Please enter your postal / ZIP code";
    return null;
  };

  const pay = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setPaying(true);
    setPayError("");
    try {
      await payWithRazorpay({
        orderPayload: {
          order_type: "coffee",
          items: cart.map((c) => ({
            speciality: c.speciality,
            variant: c.variant || null,
            roast: c.roast,
            grind: c.grind,
            size: c.size,
            bags: c.bags || 1,
            blend_arabica_pct: c.blend_arabica_pct ?? null,
          })),
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          address: {
            line1: line1.trim(),
            city: city.trim(),
            state: stateRegion.trim() || null,
            pincode: pincode.trim(),
            country: country.trim(),
          },
          shipping_zone: zone,
          destination_pincode: zone === "india" ? pincode : null,
          voucher_code: voucher?.code || null,
          redeem_points: redeemPoints > 0 ? redeemPoints : 0,
          order_note: note.trim() || null,
        },
        onPaid: ({ amount }) => {
          clearCart();
          navigate(`/payment-success?rzp=1&type=coffee&amount=${(amount / 100).toFixed(2)}`);
        },
        onError: (msg) => {
          setPayError(msg || "Payment could not be completed. Please try again.");
          toast.error(msg);
        },
        onDismiss: () => setPaying(false),
      });
    } catch (err2) {
      const msg = err2?.response?.data?.detail || "Could not start checkout — please try again or contact us on WhatsApp.";
      setPayError(typeof msg === "string" ? msg : "Could not start checkout — please check your details.");
      toast.error(typeof msg === "string" ? msg : "Could not start checkout");
    } finally {
      setPaying(false);
    }
  };

  const inputCls = "bg-white border-[#E5E2D9] h-12 rounded-xl px-4";

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <Link
          to="/coffee/customize"
          data-testid="checkout-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] transition-colors mb-10"
        >
          <ArrowLeft size={16} /> Back to Build your bag
        </Link>

        <header className="mb-10 md:mb-14 max-w-2xl">
          <div className="overline mb-3">The Grounded Cup</div>
          <h1 className="font-serif-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.02]">
            Check<span className="italic text-[#AD6A48]">out.</span>
          </h1>
        </header>

        {cart.length === 0 ? (
          <div data-testid="empty-cart" className="bg-white border border-[#E5E2D9] rounded-3xl p-12 text-center">
            <ShoppingCart size={32} className="mx-auto text-[#89937C] mb-4" />
            <p className="text-[#5C665A]">Your cart is empty.</p>
            <Link
              to="/coffee/customize"
              data-testid="empty-cart-build-link"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors"
            >
              Build your bag
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-7 space-y-10">
              {/* Cart items */}
              <section>
                <h2 className="font-serif-display text-2xl md:text-3xl font-medium mb-4">1. Your items</h2>
                <div className="space-y-3">
                  {cart.map((c) => (
                    <div key={c.line_id} data-testid="checkout-cart-item" className="bg-white border border-[#E5E2D9] rounded-2xl p-5 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-medium text-[#2D372B]">
                          {SIZE_LABELS[c.size]} {specialityName(c.speciality)}
                          {c.variant ? <span className="text-[#AD6A48]"> · {variantName(c.speciality, c.variant)}</span> : null}
                        </div>
                        <div className="text-sm text-[#5C665A] mt-0.5">
                          {c.roast} roast · {c.grind.replace(/_/g, " ")}
                          {c.blend_arabica_pct != null ? ` · ${c.blend_arabica_pct}% Arabica / ${100 - c.blend_arabica_pct}% Robusta` : ""}
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <button type="button" data-testid="bags-minus" onClick={() => setBags(c.line_id, (c.bags || 1) - 1)} className="w-7 h-7 rounded-full border border-[#E5E2D9] flex items-center justify-center hover:bg-[#F7F5F0]" aria-label="Fewer bags">
                            <Minus size={13} />
                          </button>
                          <span className="text-sm font-medium w-6 text-center" data-testid="bags-count">{c.bags || 1}</span>
                          <button type="button" data-testid="bags-plus" onClick={() => setBags(c.line_id, (c.bags || 1) + 1)} className="w-7 h-7 rounded-full border border-[#E5E2D9] flex items-center justify-center hover:bg-[#F7F5F0]" aria-label="More bags">
                            <Plus size={13} />
                          </button>
                          <span className="text-xs text-[#5C665A] ml-1">bag{(c.bags || 1) > 1 ? "s" : ""}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-serif-display text-xl" data-testid="line-total">{formatInr(c.unit_price * (c.bags || 1))}</div>
                        <button type="button" data-testid="remove-item-btn" onClick={() => removeFromCart(c.line_id)} className="mt-2 inline-flex items-center gap-1 text-xs text-[#AD6A48] hover:text-[#2D372B] transition-colors">
                          <Trash2 size={12} /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Order note */}
              <section>
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-serif-display text-2xl md:text-3xl font-medium">2. Special note</h2>
                  <span className="overline">Optional</span>
                </div>
                <div className="relative">
                  <StickyNote size={16} className="absolute left-4 top-4 text-[#89937C]" />
                  <textarea
                    data-testid="order-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                    placeholder="Any note or special recommendation for your order — grind preference, gifting note, roast request…"
                    rows={3}
                    className="w-full bg-white border border-[#E5E2D9] rounded-2xl pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#89937C]/30 resize-none"
                  />
                </div>
              </section>

              {/* Contact & delivery */}
              <section>
                <h2 className="font-serif-display text-2xl md:text-3xl font-medium mb-4">3. Contact &amp; delivery</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="checkout-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name *" className={inputCls} />
                  <Input data-testid="checkout-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email *" className={inputCls} />
                  <Input data-testid="checkout-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone / WhatsApp *" className={inputCls} />
                  <Input data-testid="checkout-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country *" className={inputCls} />
                  <Input data-testid="checkout-address" value={line1} onChange={(e) => setLine1(e.target.value)} placeholder="Address (house, street, area) *" className={`${inputCls} sm:col-span-2`} />
                  <Input data-testid="checkout-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City *" className={inputCls} />
                  <Input data-testid="checkout-state" value={stateRegion} onChange={(e) => setStateRegion(e.target.value)} placeholder="State / Region" className={inputCls} />
                  <Input
                    data-testid="checkout-pincode"
                    value={pincode}
                    onChange={(e) => setPincode(zone === "india" ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value.slice(0, 12))}
                    placeholder={zone === "india" ? "Pincode *" : "Postal / ZIP code *"}
                    className={inputCls}
                    inputMode={zone === "india" ? "numeric" : "text"}
                  />
                </div>
                <p className="mt-2 text-xs text-[#5C665A]">
                  {zone === "india" ? "Shipping within India · delivered in 3–7 days." : "International shipping · delivered in 10–21 days."}
                </p>
              </section>

              {/* Voucher */}
              <section>
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-serif-display text-2xl md:text-3xl font-medium">4. Discount voucher</h2>
                  <span className="overline">Optional</span>
                </div>
                {voucher ? (
                  <div data-testid="voucher-applied" className="flex items-center justify-between bg-white border border-[#89937C] ring-2 ring-[#89937C]/30 rounded-2xl px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#89937C]/15 flex items-center justify-center">
                        <Check size={16} className="text-[#89937C]" />
                      </div>
                      <div>
                        <div className="text-[#2D372B] font-medium">{voucher.code} applied</div>
                        <div className="text-xs text-[#5C665A]">
                          {voucher.description || (voucher.discount_type === "percent" ? `${voucher.value}% off` : `₹${voucher.value} off`)}
                        </div>
                      </div>
                    </div>
                    <button type="button" data-testid="voucher-remove-btn" onClick={() => { setVoucher(null); setVoucherInput(""); }} className="p-2 rounded-full border border-[#E5E2D9] hover:bg-[#F7F5F0] transition-colors" aria-label="Remove voucher">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-3">
                      <div className="relative flex-1">
                        <TicketPercent size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#89937C]" />
                        <Input
                          data-testid="voucher-input"
                          value={voucherInput}
                          onChange={(e) => { setVoucherInput(e.target.value.toUpperCase()); setVoucherError(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyVoucher(); } }}
                          placeholder="Enter voucher code"
                          className="bg-white border-[#E5E2D9] pl-11 h-12 rounded-full"
                        />
                      </div>
                      <button type="button" data-testid="voucher-apply-btn" onClick={applyVoucher} disabled={voucherLoading || !voucherInput.trim()} className="inline-flex items-center gap-2 rounded-full px-7 border border-[#2D372B] text-[#2D372B] font-medium hover:bg-[#2D372B] hover:text-white transition-colors disabled:opacity-50">
                        {voucherLoading && <Loader2 size={14} className="animate-spin" />}
                        Apply
                      </button>
                    </div>
                    {voucherError && <p data-testid="voucher-error" className="mt-2 text-sm text-[#AD6A48]">{voucherError}</p>}
                  </div>
                )}
              </section>
            </div>

            {/* Summary */}
            <aside className="md:col-span-5">
              <div className="md:sticky md:top-24 bg-white border border-[#E5E2D9] rounded-3xl p-6 md:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingCart size={18} className="text-[#AD6A48]" />
                  <span className="overline">Order summary</span>
                </div>
                <ul className="space-y-3 text-sm text-[#5C665A]">
                  <li className="flex justify-between">
                    <span>Items ({cart.reduce((s, c) => s + (c.bags || 1), 0)} bags)</span>
                    <span className="text-[#2D372B] font-medium" data-testid="summary-subtotal">{formatInr(subtotal)}</span>
                  </li>
                  {zone === "india" && (
                    <li className="flex justify-between">
                      <span>GST (5%)</span>
                      <span className="text-[#2D372B] font-medium" data-testid="summary-gst">{formatInr(gst)}</span>
                    </li>
                  )}
                  <li className="flex justify-between">
                    <span>Shipping ({zone === "india" ? "India" : "Intl."} · {(totalGrams / 1000).toFixed(totalGrams % 1000 ? 2 : 0)} kg)</span>
                    <span className="text-[#2D372B] font-medium" data-testid="summary-shipping">{formatInr(shipping)}</span>
                  </li>
                  {discount > 0 && (
                    <li className="flex justify-between">
                      <span>Discount ({voucher.code})</span>
                      <span className="text-[#89937C] font-medium" data-testid="summary-discount">−{formatInr(discount)}</span>
                    </li>
                  )}
                  {member && (member.loyalty_points || 0) > 0 && (
                    <li className="flex flex-col gap-1.5">
                      <div className="flex justify-between items-center">
                        <span>Loyalty points ({member.loyalty_points} available)</span>
                        <span className="text-[#89937C] font-medium" data-testid="summary-loyalty">
                          {redeemPoints > 0 ? `−${formatInr(redeemPoints)}` : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          data-testid="redeem-points-input"
                          type="number"
                          min="0"
                          max={member.loyalty_points}
                          value={redeemPoints || ""}
                          onChange={(e) => setRedeemPoints(Math.max(0, Math.min(parseInt(e.target.value || "0", 10), member.loyalty_points)))}
                          placeholder="Points to redeem"
                          className="h-9 w-36 rounded-lg border border-[#E5E2D9] bg-[#F7F5F0] px-2.5 text-xs text-[#2D372B]"
                        />
                        <button type="button" data-testid="redeem-max-btn" onClick={() => setRedeemPoints(member.loyalty_points)} className="text-xs text-[#89937C] hover:text-[#2D372B]">
                          Use all
                        </button>
                      </div>
                    </li>
                  )}
                </ul>
                <div className="mt-6 pt-6 border-t border-[#E5E2D9] flex items-baseline justify-between">
                  <span className="overline">Total</span>
                  <span className="font-serif-display text-3xl text-[#2D372B]" data-testid="summary-total">{formatInr(total)}</span>
                </div>
                {!isInr && (
                  <p className="mt-2 text-xs text-[#5C665A] text-right">
                    Payments are charged as ₹{total.toLocaleString("en-IN")} INR
                  </p>
                )}
                <button
                  type="button"
                  data-testid="checkout-pay-btn"
                  onClick={pay}
                  disabled={paying}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
                >
                  {paying ? <Loader2 size={16} className="animate-spin" /> : <IndianRupee size={16} />}
                  {paying ? "Opening…" : `Pay ${formatInr(total)} — UPI · Card · NetBanking`}
                </button>
                {payError && (
                  <p data-testid="checkout-pay-error" className="mt-3 text-xs text-[#AD6A48] text-center font-medium">{payError}</p>
                )}
                <p className="mt-4 text-xs text-[#5C665A] text-center">
                  Secure payments via Razorpay. Roasted &amp; shipped within 48 hours.
                </p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
